import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '@env/environment';
import { ApiService } from '@services/api.service';
import {
  BillItemModel,
  BillUpdateInvoiceModel,
  BillUpdateManualPaidModel,
  PaymentBillItemPendingModel,
  BillUpdateRefundModel,
} from '@data-layer/models/payments';
import {
  ManagementBillModel,
  ManagementBillSearchModel,
  ManagementBillQueryParamsModel,
  ManagementPartialRefundUpdateModel,
} from '@data-layer/models/management';
import { PaginatedResultModel } from '@data-layer/models/common';
import { StripePaymentLinkModel } from '@data-layer/models/payments';
import { map } from 'rxjs/operators';
import { groupCollectionFees } from '@app/shared/helpers';
import { isDefined } from '@services/utils/utils.service';
import { ManagementBillStateUpdateModel } from '../models/management/management-bill-state-update-model';

@Injectable({
  providedIn: 'root',
})
export class BillsManagementRepository {
  constructor(private apiService: ApiService) {}

  public search(params?: ManagementBillSearchModel, isShowLoader: boolean = true): Observable<PaginatedResultModel<ManagementBillModel>> {
    return this.apiService
      .get<PaginatedResultModel<ManagementBillModel>>(
        `${environment.url.prefixCustoms}v2/management/bills`,
        {
          params,
        },
        { isShowLoader },
      )
      .pipe(
        map((data: PaginatedResultModel<ManagementBillModel>) => {
          return groupCollectionFees<ManagementBillModel, BillItemModel>(data);
        }),
      );
  }

  public create(companyId: number, query: ManagementBillQueryParamsModel): Observable<ManagementBillModel[]> {
    const params: ManagementBillQueryParamsModel = {
      referenceNumbers: query.referenceNumbers,
      ...(isDefined(query?.isCalculateFee) && { isCalculateFee: query.isCalculateFee }),
      ...(isDefined(query?.billToId) && { billToId: query.billToId }),
      ...(isDefined(query?.coupon) && { coupon: query.coupon }),
      ...(isDefined(query?.paymentMethodId) && { paymentMethodId: query.paymentMethodId }),
    };

    return this.apiService.post<ManagementBillModel[]>(
      `${environment.url.prefixCustoms}v3/management/companies/${companyId}/bills`,
      params,
    );
  }

  public createStripePaymentLinks(
    companyId: number,
    referenceNumber: string,
    paymentMethodId: number,
  ): Observable<StripePaymentLinkModel[]> {
    return this.apiService.post<StripePaymentLinkModel[]>(
      `${environment.url.prefixCustoms}v3/management/companies/${companyId}/stripe-payment-links`,
      { referenceNumber, paymentMethodId },
    );
  }

  public sapInvoice(billId: number, isShowLoader: boolean = true): Observable<never> {
    return this.apiService.post(`${environment.url.prefixCustoms}v2/management/bills/${billId}/sap-invoice`, null, null, { isShowLoader });
  }

  public createInvoice(billId: number, invoice: BillUpdateInvoiceModel, isShowLoader: boolean = true): Observable<never> {
    return this.apiService.post(`${environment.url.prefixCustoms}v2/management/bills/${billId}/invoice`, invoice, null, { isShowLoader });
  }

  public updateInvoice(billId: number, invoice: BillUpdateInvoiceModel, isShowLoader: boolean = true): Observable<never> {
    return this.apiService.put(`${environment.url.prefixCustoms}v2/management/bills/${billId}/invoice`, invoice, null, { isShowLoader });
  }

  public refund(billId: number, invoice: BillUpdateRefundModel, isShowLoader: boolean = true): Observable<never> {
    return this.apiService.put(`${environment.url.prefixCustoms}v2/management/bills/${billId}/refund-data`, invoice, null, {
      isShowLoader,
    });
  }

  public partialRefund(billId: number, data: ManagementPartialRefundUpdateModel, isShowLoader: boolean = true): Observable<never> {
    return this.apiService.put(`${environment.url.prefixCustoms}v2/management/bills/${billId}/partial-refund-data`, data, null, {
      isShowLoader,
    });
  }

  public updateBillState(billId: number, data: ManagementBillStateUpdateModel, isShowLoader: boolean = true): Observable<never> {
    return this.apiService.put(`${environment.url.prefixCustoms}v2/management/bills/${billId}/state`, data, null, {
      isShowLoader,
    });
  }

  public setManualPaid(billId: number, invoice: BillUpdateManualPaidModel, isShowLoader: boolean = true): Observable<never> {
    return this.apiService.post(`${environment.url.prefixCustoms}v2/management/bills/${billId}`, invoice, null, {
      isShowLoader,
    });
  }

  public getPendingBills(referenceNumber: string, isShowLoader?: boolean): Observable<PaymentBillItemPendingModel> {
    return this.apiService.get<PaymentBillItemPendingModel>(
      `${environment.url.prefixFreight}management/bills/${referenceNumber}/user-data`,
      {},
      { isShowLoader },
    );
  }
}
